import { Link } from "react-router-dom";
import { Minus, Plus, X, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function Cart() {
  const { items, update, remove, subtotal, shipping, total } = useCart();

  return (
    <div data-testid="page-cart" className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20 min-h-screen">
      <div className="max-w-[1480px] mx-auto px-4 md:px-10">
        <div className="overline overline-green">Your Pantry</div>
        <h1 className="font-serif-display text-3xl md:text-5xl mt-3 font-semibold text-[var(--ink)]">Shopping Cart</h1>
        <p className="text-[var(--ink-3)] mt-2">{items.length} {items.length === 1 ? "item" : "items"} in your cart</p>

        {!items.length ? (
          <div className="card-soft p-14 text-center mt-10">
            <ShoppingBag size={42} className="text-[var(--gold)] mx-auto mb-4" />
            <p className="font-serif-display text-2xl text-[var(--green)]">Your cart is empty.</p>
            <p className="text-sm text-[var(--ink-3)] mt-2">Add a few pantry essentials to get started.</p>
            <Link to="/products" data-testid="cart-shop" className="btn-primary mt-6 inline-flex">Shop Now <ArrowRight size={14} /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
            <div className="lg:col-span-8 space-y-3">
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="card-soft p-3 md:p-4 flex gap-4">
                  <Link to={`/products/${i.id}`} className="w-20 h-20 md:w-24 md:h-24 rounded-lg bg-white overflow-hidden border border-[var(--line-soft)] shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Link to={`/products/${i.id}`} className="font-serif-display text-base md:text-lg font-semibold text-[var(--ink)] leading-tight hover:text-[var(--green)] line-clamp-2">{i.name}</Link>
                        <div className="kbd text-[0.6rem] text-[var(--ink-3)] mt-1">{i.weight}</div>
                      </div>
                      <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} aria-label="Remove" className="text-[var(--ink-3)] hover:text-red-600 transition shrink-0"><X size={18} /></button>
                    </div>
                    <div className="mt-auto pt-3 flex items-center justify-between gap-3">
                      <div className="inline-flex items-center border border-[var(--line)] rounded-full bg-white">
                        <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => update(i.id, i.qty - 1)} className="px-3 py-1.5 hover:text-[var(--green)]"><Minus size={12} /></button>
                        <span className="px-3 text-sm font-semibold min-w-[32px] text-center">{i.qty}</span>
                        <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => update(i.id, i.qty + 1)} className="px-3 py-1.5 hover:text-[var(--green)]"><Plus size={12} /></button>
                      </div>
                      <div className="font-serif-display text-lg font-semibold text-[var(--green)]">₹{(typeof i.price === "number" ? i.price : parseInt(String(i.price).replace(/[^\d]/g, ""), 10) || 0) * i.qty}</div>
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/products" data-testid="cart-continue" className="inline-flex items-center gap-2 text-sm text-[var(--green)] font-semibold link-underline mt-4">← Continue shopping</Link>
            </div>

            <aside className="lg:col-span-4">
              <div className="card-soft p-6 sticky top-[100px]">
                <h3 className="font-serif-display text-xl font-semibold text-[var(--ink)] mb-5">Order Summary</h3>
                <div className="space-y-3 text-sm">
                  <Row label="Subtotal" value={`₹${subtotal}`} />
                  <Row label="Shipping" value={shipping === 0 ? "Free" : `₹${shipping}`} />
                  {subtotal > 0 && subtotal < 500 && <p className="text-xs text-[var(--ink-3)]">Add ₹{500 - subtotal} more for free shipping.</p>}
                </div>
                <div className="border-t border-[var(--line-soft)] mt-5 pt-5 flex justify-between items-baseline">
                  <span className="font-semibold">Total</span>
                  <span className="font-serif-display text-2xl font-semibold text-[var(--green)]">₹{total}</span>
                </div>
                <Link to="/checkout" data-testid="cart-checkout" className="btn-primary w-full mt-6">Proceed to Checkout <ArrowRight size={14} /></Link>
                <p className="kbd text-[0.6rem] text-[var(--ink-3)] text-center mt-3">100% Secure · 7-day Returns</p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between"><span className="text-[var(--ink-2)]">{label}</span><span className="font-semibold">{value}</span></div>
  );
}
